import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

export default function PaymentReturn({ cancel = false }) {
  const [sp] = useSearchParams();
  const { clearCart } = useApp();
  const [state, setState] = useState(cancel ? "cancelled" : "checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (cancel) return;
    const sid = sp.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setOrder(data.order_number); setState("paid"); clearCart(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (++tries < 10) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, [cancel]);

  const V = {
    checking: { icon: <Loader2 className="animate-spin text-cyan-300" size={40} />, title: "Confirming your payment…", sub: "Please wait a moment while we verify the transaction with Stripe." },
    paid: { icon: <CheckCircle2 className="text-emerald-400" size={44} />, title: "Payment confirmed", sub: order ? `Order ${order} is confirmed. A receipt has been sent to your email.` : "Your order is confirmed." },
    failed: { icon: <XCircle className="text-rose-400" size={44} />, title: "Payment failed", sub: "Your payment could not be completed. No charge was made — please try again." },
    pending: { icon: <Clock className="text-amber-300" size={40} />, title: "Payment processing", sub: "This is taking longer than usual. Your order will update automatically once Stripe confirms." },
    cancelled: { icon: <XCircle className="text-slate-400" size={44} />, title: "Checkout cancelled", sub: "No charge was made. Your cart is still saved." },
    error: { icon: <XCircle className="text-rose-400" size={44} />, title: "Something went wrong", sub: "We couldn't find your session. Please try checkout again." },
  }[state];

  return (
    <div className="px-4 pt-32 pb-24 max-w-xl mx-auto text-center" data-testid="payment-return">
      <div className="glass-strong rounded-3xl p-10 flex flex-col items-center">
        <div className="mb-5">{V.icon}</div>
        <h1 className="font-serif text-3xl text-white mb-3" data-testid="payment-status-title">{V.title}</h1>
        <p className="text-slate-400 text-sm max-w-sm leading-relaxed mb-8">{V.sub}</p>
        <div className="flex gap-3">
          {state === "paid"
            ? <Link to="/my-lab" data-testid="payment-view-orders" className="px-6 py-2.5 rounded-full btn-primary text-sm">View my orders</Link>
            : <Link to="/checkout" data-testid="payment-retry" className="px-6 py-2.5 rounded-full btn-primary text-sm">Back to checkout</Link>}
          <Link to="/shop" className="px-6 py-2.5 rounded-full btn-ghost text-sm">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
